import { useMemo } from "react";
import { useDecisionMetrics, VehicleDecisionData } from "./useDecisionMetrics";

export type AttentionLevel = "needs_attention" | "monitor" | "stable" | "ignore";
export type ItemStatus = "open" | "handled" | "delegated" | "review_later";

export interface AttentionItem {
  id: string;
  vehicleId: string;
  vehicleName: string;
  level: AttentionLevel;
  score: number; // higher = more urgent
  title: string;
  reason: string;
  factors: { label: string; value: string }[];
  status: ItemStatus;
}

export type RiskTimeHorizon = "immediate" | "slow_burn";
export type RiskTrend = "worsening" | "stable" | "improving";

export interface RiskCard {
  id: string;
  vehicleId?: string;
  vehicleName?: string;
  title: string;
  description: string;
  whyItMatters: string;
  timeHorizon: RiskTimeHorizon;
  trend: RiskTrend;
  severity: "high" | "medium" | "low";
}

export interface ClassifiedItem {
  id: string;
  vehicleId?: string;
  vehicleName?: string;
  title: string;
  reason: string;
  category: "attention" | "ignorable";
  overridden?: boolean;
}

function scoreVehicle(v: VehicleDecisionData, fleetAvg: { monthlyProfit: number; expenseRatio: number; utilization: number }): number {
  let score = 0;

  // Financial impact — negative profit is high attention
  if (v.monthlyProfit < 0) score += 40;
  else if (v.monthlyProfit < fleetAvg.monthlyProfit * 0.5) score += 25;
  else if (v.monthlyProfit < fleetAvg.monthlyProfit * 0.7) score += 10;

  // Risk — expense spike
  if (v.recentExpenseChange > 50) score += 30;
  else if (v.recentExpenseChange > 25) score += 15;

  // Change since last check — low utilization trend
  if (v.utilization < 20) score += 20;
  else if (v.utilization < fleetAvg.utilization * 0.5) score += 10;

  // High expense ratio
  if (v.expenseRatio > 80) score += 15;
  else if (v.expenseRatio > 60) score += 5;

  return score;
}

function getAttentionLevel(score: number): AttentionLevel {
  if (score >= 40) return "needs_attention";
  if (score >= 20) return "monitor";
  if (score >= 10) return "stable";
  return "ignore";
}

export const useScaleMetrics = () => {
  const { vehicleData, fleetAvg, isLoading } = useDecisionMetrics();

  const attentionItems = useMemo<AttentionItem[]>(() => {
    if (!vehicleData.length) return [];

    return vehicleData
      .filter((v) => v.tripCount > 0)
      .map((v) => {
        const name = v.vehicle.name || `${v.vehicle.year} ${v.vehicle.make} ${v.vehicle.model}`;
        const score = scoreVehicle(v, fleetAvg);
        const level = getAttentionLevel(score);

        const factors: { label: string; value: string }[] = [];
        if (v.monthlyProfit < 0) factors.push({ label: "Monthly profit", value: `-$${Math.abs(Math.round(v.monthlyProfit))}` });
        else factors.push({ label: "Monthly profit", value: `$${Math.round(v.monthlyProfit)}` });

        if (v.recentExpenseChange > 25) factors.push({ label: "Expense change", value: `+${Math.round(v.recentExpenseChange)}%` });
        factors.push({ label: "Utilization", value: `${Math.round(v.utilization)}%` });
        factors.push({ label: "Expense ratio", value: `${Math.round(v.expenseRatio)}%` });

        const reasons: string[] = [];
        if (v.monthlyProfit < 0) reasons.push("losing money monthly");
        if (v.recentExpenseChange > 25) reasons.push("expenses trending up");
        if (v.utilization < fleetAvg.utilization * 0.5) reasons.push("low utilization");
        if (v.expenseRatio > 70) reasons.push("high expense ratio");

        return {
          id: `attn-${v.vehicle.id}`,
          vehicleId: v.vehicle.id,
          vehicleName: name,
          level,
          score,
          title: level === "needs_attention"
            ? `${name} — needs attention`
            : level === "monitor"
              ? `${name} — worth monitoring`
              : level === "stable"
                ? `${name} — stable`
                : `${name} — no action needed`,
          reason: reasons.length > 0 ? reasons.join(", ") : "metrics are within normal ranges",
          factors,
          status: "open" as ItemStatus,
        };
      })
      .sort((a, b) => b.score - a.score);
  }, [vehicleData, fleetAvg]);

  const riskCards = useMemo<RiskCard[]>(() => {
    if (!vehicleData.length) return [];
    const cards: RiskCard[] = [];

    // High expense volatility
    const volatileVehicles = vehicleData.filter((v) => v.recentExpenseChange > 30 && v.lastMonthExpenses > 50);
    if (volatileVehicles.length > 0) {
      const worst = volatileVehicles.sort((a, b) => b.recentExpenseChange - a.recentExpenseChange)[0];
      const name = worst.vehicle.name || `${worst.vehicle.year} ${worst.vehicle.make} ${worst.vehicle.model}`;
      cards.push({
        id: "risk-expense-volatility",
        vehicleId: worst.vehicle.id,
        vehicleName: name,
        title: "High expense volatility",
        description: `${name} expenses swung ${Math.round(worst.recentExpenseChange)}% in the last month ($${Math.round(worst.priorMonthExpenses)} → $${Math.round(worst.lastMonthExpenses)}).`,
        whyItMatters: "Unpredictable costs make it hard to plan cash flow and evaluate true profitability.",
        timeHorizon: worst.recentExpenseChange > 50 ? "immediate" : "slow_burn",
        trend: worst.recentExpenseChange > 50 ? "worsening" : "stable",
        severity: worst.recentExpenseChange > 50 ? "high" : "medium",
      });
    }

    // Single-point-of-failure vehicle
    if (vehicleData.length >= 2) {
      const sorted = [...vehicleData].sort((a, b) => b.totalEarnings - a.totalEarnings);
      const topEarner = sorted[0];
      const totalFleetEarnings = vehicleData.reduce((s, v) => s + v.totalEarnings, 0);
      const share = totalFleetEarnings > 0 ? (topEarner.totalEarnings / totalFleetEarnings) * 100 : 0;

      if (share > 60) {
        const name = topEarner.vehicle.name || `${topEarner.vehicle.year} ${topEarner.vehicle.make} ${topEarner.vehicle.model}`;
        cards.push({
          id: "risk-single-point",
          vehicleId: topEarner.vehicle.id,
          vehicleName: name,
          title: "Single-point-of-failure vehicle",
          description: `${name} generates ${Math.round(share)}% of total fleet earnings. If it goes offline, revenue drops significantly.`,
          whyItMatters: "Revenue concentration in one vehicle creates fragility. Diversifying income sources reduces risk.",
          timeHorizon: "slow_burn",
          trend: "stable",
          severity: share > 75 ? "high" : "medium",
        });
      }
    }

    // Vehicles losing money (cash drain risk)
    const losingMoney = vehicleData.filter((v) => v.monthlyProfit < 0 && v.monthsActive >= 2);
    for (const v of losingMoney.slice(0, 2)) {
      const name = v.vehicle.name || `${v.vehicle.year} ${v.vehicle.make} ${v.vehicle.model}`;
      cards.push({
        id: `risk-cash-drain-${v.vehicle.id}`,
        vehicleId: v.vehicle.id,
        vehicleName: name,
        title: "Ongoing cash drain",
        description: `${name} is losing ~$${Math.abs(Math.round(v.monthlyProfit))}/mo over ${v.monthsActive} months.`,
        whyItMatters: "Sustained losses compound over time. Deciding to act or exit preserves capital for performing assets.",
        timeHorizon: "immediate",
        trend: v.recentExpenseChange > 10 ? "worsening" : "stable",
        severity: v.monthlyProfit < -200 ? "high" : "medium",
      });
    }

    // Low utilization fleet-wide
    const avgUtil = vehicleData.length > 0 ? vehicleData.reduce((s, v) => s + v.utilization, 0) / vehicleData.length : 0;
    if (avgUtil < 30 && vehicleData.length >= 2) {
      cards.push({
        id: "risk-low-fleet-util",
        title: "Low fleet-wide utilization",
        description: `Average utilization is ${Math.round(avgUtil)}% across ${vehicleData.length} vehicles. Many days are unbooked.`,
        whyItMatters: "Idle vehicles still cost money (insurance, depreciation, storage). Low utilization erodes margins.",
        timeHorizon: "slow_burn",
        trend: "stable",
        severity: avgUtil < 15 ? "high" : "medium",
      });
    }

    return cards.sort((a, b) => {
      const sevOrder = { high: 0, medium: 1, low: 2 };
      return sevOrder[a.severity] - sevOrder[b.severity];
    });
  }, [vehicleData]);

  // Classification: attention vs ignorable
  const classifiedItems = useMemo<ClassifiedItem[]>(() => {
    if (!vehicleData.length) return [];

    return vehicleData
      .filter((v) => v.tripCount > 0)
      .map((v) => {
        const name = v.vehicle.name || `${v.vehicle.year} ${v.vehicle.make} ${v.vehicle.model}`;
        const score = scoreVehicle(v, fleetAvg);
        const needsAttention = score >= 20;

        const reasons: string[] = [];
        if (v.monthlyProfit < 0) reasons.push("negative monthly profit");
        else if (v.monthlyProfit < fleetAvg.monthlyProfit * 0.7) reasons.push("below-average profit");
        if (v.recentExpenseChange > 25) reasons.push("recent expense increase");
        if (v.utilization < fleetAvg.utilization * 0.5) reasons.push("underutilized");
        if (v.expenseRatio > 70) reasons.push("high expense ratio");

        if (!needsAttention) {
          reasons.length = 0;
          if (v.monthlyProfit >= fleetAvg.monthlyProfit * 0.9) reasons.push("profit is strong");
          if (v.utilization >= fleetAvg.utilization * 0.8) reasons.push("utilization is healthy");
          if (Math.abs(v.recentExpenseChange) < 15) reasons.push("expenses are stable");
        }

        return {
          id: `class-${v.vehicle.id}`,
          vehicleId: v.vehicle.id,
          vehicleName: name,
          title: name,
          reason: reasons.length > 0 ? reasons.join(", ") : needsAttention ? "multiple factors flagged" : "no issues detected",
          category: needsAttention ? "attention" as const : "ignorable" as const,
        };
      });
  }, [vehicleData, fleetAvg]);

  return { attentionItems, riskCards, classifiedItems, vehicleData, isLoading };
};
